import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  isLiveModeConfirmed,
  setLiveModeConfirmed,
  TRADING_MODES,
  getModeLabel,
} from "@/lib/tradingMode";

export default function TradingModeShell({
  user,
  mode,
  activeMode,
  onModeChange,
  onLogout,
  children,
}) {
  const navigate = useNavigate();
  const [needsLiveConfirmation, setNeedsLiveConfirmation] = useState(false);

  useEffect(() => {
    if (mode === TRADING_MODES.LIVE && !isLiveModeConfirmed()) {
      setNeedsLiveConfirmation(true);
      return;
    }

    if (activeMode === mode) {
      setNeedsLiveConfirmation(false);
      return;
    }

    onModeChange(mode);
    setNeedsLiveConfirmation(false);
  }, [activeMode, mode, onModeChange]);

  const handleConfirmLive = () => {
    setLiveModeConfirmed();
    setNeedsLiveConfirmation(false);
    onModeChange(TRADING_MODES.LIVE);
  };

  const handleCancelLive = () => {
    setNeedsLiveConfirmation(false);
    if (activeMode && activeMode !== TRADING_MODES.LIVE) {
      const livePrefix = `/${TRADING_MODES.LIVE}`;
      const activePrefix = `/${activeMode}`;
      const currentPath = window.location.pathname;
      const fallbackPath = currentPath.startsWith(`${livePrefix}/`)
        ? currentPath.replace(livePrefix, activePrefix)
        : activePrefix;
      navigate(fallbackPath, { replace: true });
      return;
    }
    navigate("/mode-select", { replace: true });
  };

  if (needsLiveConfirmation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg border-destructive/40 shadow-xl" data-testid="live-confirmation-card">
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/15">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Stai entrando in Live Trading</CardTitle>
            <CardDescription>
              Le azioni avranno effetto reale. Vuoi continuare?
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={handleCancelLive} data-testid="cancel-live-entry">
              Annulla
            </Button>
            <Button
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmLive}
              data-testid="confirm-live-entry"
            >
              Conferma e continua
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      mode={mode}
      modeLabel={getModeLabel(mode)}
    >
      {children}
    </DashboardLayout>
  );
}
